import React from 'react';
import useIsMounted from '@/hooks/useIsMounted';
import { isDirtyAndTouched } from '@/utils/isDirtyAndTouched';
import { FormikTouched } from 'formik';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type ConfirmModalToggleContextType = {
  open: (onConfirm: () => void) => void;
};

const ConfirmModalToggleContext = React.createContext<
  ConfirmModalToggleContextType | undefined
>(undefined);

export const useConfirmedModalToggle = () => {
  const context = React.useContext(ConfirmModalToggleContext);
  if (!context) {
    throw new Error(
      'useConfirmModalToggle must be used within a ConfirmModalToggleProvider'
    );
  }
  const toggle = React.useCallback(
    (onConfirm: () => void, dirty: boolean, touched: FormikTouched<any>) =>
      () => {
        if (!isDirtyAndTouched(dirty, touched)) {
          onConfirm();
        } else {
          context.open(onConfirm);
        }
      },
    [context]
  );
  return toggle;
};

export const ConfirmModalToggleProvider: React.FC<{
  children: React.ReactNode | React.ReactNode[];
}> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [onConfirm, setOnConfirm] = React.useState<() => void>();

  const toggle = () => setIsOpen((o) => !o);
  const open = (onConfirm: () => void) => {
    setOnConfirm(() => onConfirm);
    setIsOpen(true);
  };

  return (
    <ConfirmModalToggleContext.Provider value={{ open }}>
      <ConfirmModalToggle
        isOpen={isOpen}
        toggle={toggle}
        onConfirm={onConfirm ?? (() => {})}
      />
      {children}
    </ConfirmModalToggleContext.Provider>
  );
};

export type ConfirmModalToggleProps = {
  isOpen: boolean;
  toggle: () => void;
  onConfirm: () => void;
};

const ConfirmModalToggle: React.FC<ConfirmModalToggleProps> = ({
  isOpen,
  toggle,
  onConfirm,
}) => {
  const isMounted = useIsMounted();

  const confirmHandler: React.MouseEventHandler<any> = async (e) => {
    if (e) e.preventDefault();
    await onConfirm();
    if (!isMounted.current) return;
    toggle();
  };

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={toggle}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar los datos ingresados?</AlertDialogTitle>
            <AlertDialogDescription>
              Hay datos sin guardar en este formulario, estás seguro de cerrar
              la ventana?
              <br />
              Presioná Cancelar y guardá los datos antes de cerrar esta ventana
              si querés conservarlos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmHandler}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
